import React from "react";
import { BadgeCheck, HeartHandshake, Info, Stethoscope } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * "Different Support. Affordable Connection."
 *
 * Value anchoring against BetterHelp ONLY, written so it can never read as
 * "therapy but cheaper". The section leads with the difference in the type of
 * support, never claims equivalence, and repeats that Sponsor Me is not
 * therapy, counseling, medical care or professional addiction treatment.
 *
 * All Sponsor Me numbers are derived from the live plans returned by
 * GET /api/billing/plans - nothing about our own pricing is hard-coded, and
 * this component never touches billing, Stripe or the Access Pass logic.
 */

/**
 * BetterHelp facts.
 * Source of truth: BetterHelp's own FAQ, "How much does it cost?" -
 * "The cost of therapy through BetterHelp ranges from $70 to $100 per week".
 * Re-verify this periodically and update `verifiedOn` when you do.
 */
export const BETTERHELP = {
  weeklyLow: 70,
  weeklyHigh: 100,
  sourceUrl: "https://www.betterhelp.com/faq/",
  verifiedOn: "September 19, 2026",
};

// A month and a year expressed in weeks (52-week year) so "vs. Weekly"
// savings are mathematically correct for the equivalent period.
const WEEKS_PER_INTERVAL = { week: 1, month: 52 / 12, year: 52 };

/** Whole-percent saving of a plan versus paying the weekly plan for the same period. */
export function savingsVsWeekly(plan, weeklyPlan) {
  if (!plan || !weeklyPlan || plan.interval === "week") return null;
  const weeks = WEEKS_PER_INTERVAL[plan.interval];
  if (!weeks) return null;
  const weeklyEquivalent = weeklyPlan.amount * weeks;
  if (!weeklyEquivalent) return null;
  const pct = Math.round((1 - plan.amount / weeklyEquivalent) * 100);
  return pct > 0 ? pct : null;
}

export default function SupportComparison({ plans = [], freePassDays = 7 }) {
  const weekly = plans.find((p) => p.interval === "week");
  const others = plans.filter((p) => p.interval !== "week");
  if (!weekly) return null;

  return (
    <section
      data-testid="support-comparison"
      aria-labelledby="support-comparison-heading"
      className="mt-14 rounded-2xl bg-white border border-[#E3DED4] shadow-[0_10px_30px_rgba(31,42,46,0.07)] p-6 sm:p-8"
    >
      <h2
        id="support-comparison-heading"
        data-testid="support-comparison-heading"
        className="font-display text-2xl sm:text-3xl font-bold text-[#1F2A2E]"
      >
        Different Support. Affordable Connection.
      </h2>

      <p
        data-testid="support-comparison-intro"
        className="mt-3 text-[#4B5A60] leading-7 max-w-3xl"
      >
        Support can take different forms. BetterHelp provides access to licensed therapy. Sponsor Me
        is different&mdash;we help people find and connect with volunteer peer sponsors in recovery
        communities. Sponsor Me is not therapy, counseling, medical care, or professional addiction
        treatment.
      </p>

      <div className="mt-7 grid gap-5 md:grid-cols-2">
        {/* BetterHelp - described factually, never as a competitor we beat */}
        <div
          data-testid="comparison-card-betterhelp"
          className="rounded-2xl border border-[#E3DED4] bg-[#FAF7F1] p-5 sm:p-6 flex flex-col"
        >
          <div className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-[#4B5A60] shrink-0" aria-hidden="true" />
            <h3 className="font-display text-lg font-bold text-[#1F2A2E]">BetterHelp</h3>
          </div>
          <p className="mt-3 text-sm text-[#4B5A60] leading-6">
            An online therapy platform that provides access to licensed therapists.
          </p>
          <p
            data-testid="comparison-betterhelp-price"
            className="mt-4 text-base font-semibold text-[#1F2A2E] leading-7"
          >
            Online therapy through BetterHelp: approximately ${BETTERHELP.weeklyLow}&ndash;$
            {BETTERHELP.weeklyHigh}/week*
          </p>
        </div>

        {/* Sponsor Me - live pricing, clear about what the subscription buys */}
        <div
          data-testid="comparison-card-sponsor-me"
          className="rounded-2xl border-2 border-[#1F6F78] bg-[#EAF3F4] p-5 sm:p-6 flex flex-col"
        >
          <div className="flex items-center gap-2">
            <HeartHandshake className="w-5 h-5 text-[#1F6F78] shrink-0" aria-hidden="true" />
            <h3 className="font-display text-lg font-bold text-[#134B52]">Sponsor Me</h3>
          </div>
          <p className="mt-3 text-sm text-[#2F5E63] leading-6">
            A peer-connection platform for finding and messaging volunteer peer sponsors in recovery
            communities.
          </p>
          <p
            data-testid="comparison-sponsor-me-price"
            className="mt-4 text-base font-semibold text-[#134B52] leading-7"
          >
            Sponsor Me peer-connection platform: plans starting at {weekly.price_display}/week.
          </p>
          <p
            data-testid="comparison-subscription-scope"
            className="mt-3 text-sm text-[#2F5E63] leading-6"
          >
            Your Sponsor Me membership gives you access to the platform&apos;s messaging and
            connection features. You are not paying for the sponsor themselves, and this is not
            therapy.
          </p>
          {/* The warmer word "membership" above must never be a way of hiding
              that this is a recurring charge, so the recurring terms are stated
              immediately underneath it - and the figures come from the live
              plan catalogue, not from hand-written copy. */}
          <p
            data-testid="comparison-recurring-terms"
            className="mt-3 text-sm font-semibold text-[#134B52] leading-6"
          >
            It is a recurring subscription: {weekly.price_display}
            {weekly.period_display} renews automatically every week until you cancel. Monthly and
            yearly memberships renew automatically on the same basis. Cancel any time and you keep
            access until the renewal date you have already paid for.
          </p>
        </div>
      </div>

      {/* Plan savings, calculated from the live plan amounts */}
      {others.length > 0 && (
        <div className="mt-7">
          <h3 className="font-display text-base font-bold text-[#1F2A2E]">
            Longer plans cost less per week
          </h3>
          <div data-testid="plan-savings" className="mt-3 flex flex-wrap gap-3">
            {others.map((plan) => {
              const pct = savingsVsWeekly(plan, weekly);
              return (
                <div
                  key={plan.lookup_key}
                  data-testid={`plan-savings-${plan.lookup_key}`}
                  className="flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-[#FAF7F1] px-4 py-3"
                >
                  <span className="text-sm font-semibold text-[#1F2A2E]">
                    {plan.label} {plan.price_display}
                    {plan.period_display}
                  </span>
                  {pct ? (
                    <span className="rounded-full bg-[#E4F3EA] border border-[#B7DFC7] px-2.5 py-0.5 text-xs font-bold text-[#14603C]">
                      Save {pct}% vs. Weekly
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-[#6B7A80]">
            Savings compared with paying the {weekly.price_display} weekly plan for the same length
            of time, based on a 52-week year.
          </p>
        </div>
      )}

      <p data-testid="comparison-value-message" className="mt-7 text-[#4B5A60] leading-7 max-w-3xl">
        Professional therapy and peer sponsorship serve different purposes. Sponsor Me was built to
        make finding and connecting with volunteer peer sponsors simple, private, and affordable.
      </p>

      {/* Existing 7-Day Recovery Access Pass - both claims below are true:
          no card is collected and no subscription is created. */}
      <div
        data-testid="comparison-access-pass"
        className="mt-6 rounded-2xl bg-[#F6E2D6] border border-[#E8C4AC] p-5"
      >
        <div className="flex items-start gap-3">
          <BadgeCheck className="w-5 h-5 text-[#7A3E17] shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="font-display text-lg font-bold text-[#7A3E17]">
              Can&apos;t afford a plan right now? Get {freePassDays} days of access at no cost.
            </p>
            <p className="mt-1 text-sm font-semibold text-[#7A3E17]">
              No credit card. No automatic subscription.
            </p>
            <a
              href="#access-pass"
              data-testid="comparison-access-pass-link"
              className="mt-3 inline-flex items-center rounded-xl bg-[#D07A4A] px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#B96536] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7A3E17] focus-visible:ring-offset-2"
            >
              See the Recovery Access Pass
            </a>
          </div>
        </div>
      </div>

      <p
        data-testid="comparison-disclosure"
        className="mt-6 flex items-start gap-2 text-xs text-[#6B7A80] leading-6"
      >
        <Info className="w-3.5 h-3.5 shrink-0 mt-1" aria-hidden="true" />
        <span>
          *BetterHelp and Sponsor Me provide different services and are not directly equivalent.
          BetterHelp provides access to licensed therapy. Sponsor Me facilitates connections with
          volunteer peer sponsors and does not provide therapy, medical care, professional
          counseling, or addiction treatment. BetterHelp pricing and availability may change.
          BetterHelp pricing last verified on {BETTERHELP.verifiedOn} from BetterHelp&apos;s{" "}
          <a
            href={BETTERHELP.sourceUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
            data-testid="comparison-betterhelp-source-link"
            className="font-semibold text-[#1F6F78] underline underline-offset-2 hover:text-[#134B52] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F6F78] rounded"
          >
            official pricing FAQ
          </a>
          . Need immediate help? See our{" "}
          <Link
            to="/resources"
            data-testid="comparison-resources-link"
            className="font-semibold text-[#1F6F78] underline underline-offset-2 hover:text-[#134B52] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F6F78] rounded"
          >
            recovery resources and crisis contacts
          </Link>
          .
        </span>
      </p>
    </section>
  );
}
